import React, { useEffect, useState } from 'react';
import MaerskButton from '../common/MaerskButton';
import CustomDropdown from '../common/CustomDropdown';
import { McCheckbox } from '@maersk-global/mds-react-wrapper/components-core/mc-checkbox';
import { McInput } from '@maersk-global/mds-react-wrapper/components-core/mc-input';
import { McModal } from '@maersk-global/mds-react-wrapper/components-core/mc-modal';
import { McNotification } from '@maersk-global/mds-react-wrapper/components-core/mc-notification';
import { McTag } from '@maersk-global/mds-react-wrapper/components-core/mc-tag';
import { McTextarea } from '@maersk-global/mds-react-wrapper/components-core/mc-textarea';
import { COLORS, SPACING, BORDER_RADIUS, FONT_WEIGHTS } from '../../constants/theme';
import api from '../../services/api';

// Same capability order and NER/OCR fields as neurix-tenantmanagement-ui.
const CAP_LIST = ['OCR', 'CLASSIFICATION', 'NER', 'VALIDATION'];
const NER_DEFAULTS = { classification: '', structureModel: '', emitConfidenceScores: false, structureReasoningEffort: '' };
// [value sent to CDT, label shown]. NEURIX_LAYOUT is stored as ADI on the tenant API.
const SELECT_OPTIONS = {
  textExtractionModel: [['', '-- Select --'], ['ADI', 'NEURIX_LAYOUT'], ['NEURIX_LAYOUT_V2', 'NEURIX_LAYOUT_V2']],
  structureModel: [['', '-- Select --'], ['gpt-5.6-luna', 'gpt-5.6-luna'], ['gpt-5.4-mini', 'gpt-5.4-mini'], ['claude-haiku-5-5', 'claude-haiku-5-5']],
  structureReasoningEffort: [['', '-- Select --'], ['low', 'Low'], ['medium', 'Medium'], ['high', 'High']],
  xlParser: [['', 'Default (legacy)'], ['markitdown', 'markitdown'], ['markitdownlite', 'markitdownlite'], ['llmprobeparser', 'llmprobeparser']],
};
// NER keys whose value comes from this page rather than the CDT form.
const PAGE_KEYS = ['systemPrompt', 'responseFormat'];
const IDS_STORAGE_KEY = 'cdtDeployIds';

// textExtractionModel -> "Text Extraction Model", classification_model -> "Classification Model"
export const humanize = (key) =>
  key.replace(/_/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase());

const loadSavedIds = (messageId) => {
  try {
    return JSON.parse(localStorage.getItem(IDS_STORAGE_KEY) || '{}')[messageId] || {};
  } catch (_) {
    return {};
  }
};

const saveIds = (messageId, ids) => {
  try {
    const all = JSON.parse(localStorage.getItem(IDS_STORAGE_KEY) || '{}');
    localStorage.setItem(IDS_STORAGE_KEY, JSON.stringify({ ...all, [messageId]: ids }));
  } catch (_) {}
};

// Editable draft of a context's config: JSON values become text so they can be edited in a textarea.
const toDraft = (ctx) => {
  const config = { ...(ctx.capability === 'NER' ? NER_DEFAULTS : {}), ...(ctx.config || {}) };
  const fields = {};
  Object.entries(config).forEach(([key, value]) => {
    if (ctx.capability === 'NER' && PAGE_KEYS.includes(key)) return;
    if (Array.isArray(value) && value.every(v => typeof v === 'string')) {
      fields[key] = { type: 'lines', value: value.join('\n') };
    } else if (value !== null && typeof value === 'object') {
      fields[key] = { type: 'json', value: JSON.stringify(value, null, 2) };
    } else if (typeof value === 'boolean') {
      fields[key] = { type: 'boolean', value };
    } else if (typeof value === 'number') {
      fields[key] = { type: 'number', value: String(value) };
    } else {
      fields[key] = { type: 'string', value: value ?? '' };
    }
  });
  if (fields.textExtractionModel?.value === 'NEURIX_LAYOUT') fields.textExtractionModel.value = 'ADI';
  return {
    name: ctx.name || '',
    description: ctx.description || '',
    version: String(ctx.version || 1),
    applyPage: ctx.capability === 'NER',
    fields,
  };
};

// Throws with the field name when a JSON field doesn't parse.
const draftToConfig = (ctx, draft, pagePrompt, pageSchema) => {
  const config = {};
  Object.entries(draft.fields).forEach(([key, f]) => {
    if (f.type === 'lines') config[key] = f.value.split('\n').map(s => s.trim()).filter(Boolean);
    else if (f.type === 'boolean') config[key] = f.value;
    else if (f.type === 'number') config[key] = parseFloat(f.value);
    else if (f.type === 'json') {
      try {
        config[key] = JSON.parse(f.value || 'null');
      } catch (_) {
        throw new Error(`Invalid JSON in ${ctx.capability} › ${key}`);
      }
    } else if (key === 'xlParser' && !f.value) {
      // tenant UI omits an empty xlParser rather than sending ''
    } else config[key] = f.value;
  });
  if (ctx.capability === 'NER') {
    // Don't introduce untouched form defaults the CDT config never had.
    Object.entries(NER_DEFAULTS).forEach(([k, v]) => {
      if (!(k in (ctx.config || {})) && config[k] === v) delete config[k];
    });
    const original = ctx.config || {};
    config.systemPrompt = draft.applyPage ? pagePrompt : original.systemPrompt;
    config.responseFormat = draft.applyPage ? pageSchema : original.responseFormat;
    PAGE_KEYS.forEach(k => { if (config[k] === undefined) delete config[k]; });
  }
  return config;
};

// Key-order-insensitive JSON, so re-ordered config keys don't count as a change.
const stableStringify = (v) => {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  if (v && typeof v === 'object') {
    return `{${Object.keys(v).sort().map(k => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(',')}}`;
  }
  return JSON.stringify(v);
};

const contextChanged = (ctx, draft, config) =>
  draft.name.trim() !== (ctx.name || '') ||
  draft.description.trim() !== (ctx.description || '') ||
  (parseInt(draft.version, 10) || 1) !== (ctx.version || 1) ||
  stableStringify(config) !== stableStringify(ctx.config || {});

const labelStyle = {
  display: 'block',
  marginBottom: SPACING.xs,
  color: COLORS.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.4px'
};

const card = {
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  borderRadius: BORDER_RADIUS.lg,
  padding: SPACING.lg
};

// Lab's CustomDropdown works on display strings; map them back to the value sent to CDT.
const Dropdown = ({ value, options, onChange }) => {
  const current = options.find(([v]) => v === value);
  return (
    <CustomDropdown
      compact
      hideLabel
      value={current ? current[1] : value}
      options={options.map(([, label]) => label)}
      onChange={label => onChange(options.find(([, l]) => l === label)[0])}
      triggerStyle={{ border: `1px solid ${COLORS.border}`, fontWeight: FONT_WEIGHTS.normal, minHeight: 34, boxSizing: 'border-box' }}
    />
  );
};

const Field = ({ label, required, children, wide }) => (
  <div style={{ gridColumn: wide ? '1 / -1' : 'auto' }}>
    <label className="mds-text--x-small-bold" style={labelStyle}>
      {label} {required && <span style={{ color: COLORS.error }}>*</span>}
    </label>
    {children}
  </div>
);

const CapTag = ({ cap }) => <McTag appearance="neutral-inverse" fit="small">{cap}</McTag>;

const LiveWarning = () => (
  <McNotification appearance="warning" heading="Heads up" fit="small">
    Deploying updates your CDT workflow <strong>immediately</strong>. Every document
    processed by this workflow after you click Deploy uses the new configuration — there is no staging step.
  </McNotification>
);

const DeployToCdt = ({ messageId, messageName, systemMessage, responseFormat, deployedBy, onClose }) => {
  const saved = loadSavedIds(messageId);
  const [creds, setCreds] = useState({
    workspace_id: saved.workspace_id || '',
    workflow_id: saved.workflow_id || ''
  });
  const [access, setAccess] = useState({ checking: true, error: '' });
  const [loading, setLoading] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');
  const [workflow, setWorkflow] = useState(null);
  const [contexts, setContexts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [deployed, setDeployed] = useState(null);
  const [historyNote, setHistoryNote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/cdt-tenants/access/${messageId}`)
      .then(() => !cancelled && setAccess({ checking: false, allowed: true, error: '' }))
      .catch(err => !cancelled && setAccess({
        checking: false,
        error: err.response?.data?.error || err.message || 'Could not check deploy access.'
      }));
    return () => { cancelled = true; };
  }, [messageId]);

  const setCred = (key, value) => setCreds(prev => ({ ...prev, [key]: value }));
  const setDraft = (id, patch) => setDrafts(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  const setField = (id, key, value) =>
    setDrafts(prev => ({
      ...prev,
      [id]: { ...prev[id], fields: { ...prev[id].fields, [key]: { ...prev[id].fields[key], value } } }
    }));

  const credsReady = access.allowed && creds.workspace_id.trim() && creds.workflow_id.trim();

  const handleLoad = async () => {
    setError('');
    setLoading(true);
    setConfirming(false);
    setDeployed(null);
    setHistoryNote(null);
    try {
      const res = await api.post('/cdt-tenants/workflow', { ...creds, system_message_id: messageId });
      const ctxs = [...(res.data.contexts || [])].sort(
        (a, b) => CAP_LIST.indexOf(a.capability) - CAP_LIST.indexOf(b.capability)
      );
      setWorkflow(res.data.workflow);
      setContexts(ctxs);
      setDrafts(Object.fromEntries(ctxs.map(c => [c.id, toDraft(c)])));
      saveIds(messageId, { workspace_id: creds.workspace_id.trim(), workflow_id: creds.workflow_id.trim() });
    } catch (err) {
      setWorkflow(null);
      setContexts([]);
      setError(err.response?.data?.error || err.message || 'Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  const buildUpdates = () =>
    contexts
      .map(ctx => {
        const draft = drafts[ctx.id];
        const config = draftToConfig(ctx, draft, systemMessage, responseFormat);
        if (!contextChanged(ctx, draft, config)) return null;
        return {
          id: ctx.id,
          name: draft.name.trim(),
          description: draft.description.trim(),
          version: parseInt(draft.version, 10) || 1,
          config
        };
      })
      .filter(Boolean);

  const handleReview = () => {
    setError('');
    try {
      if (!buildUpdates().length) {
        setError('No changes to deploy — the CDT workflow already matches.');
        return;
      }
      setConfirming(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeploy = async () => {
    setError('');
    setDeploying(true);
    try {
      const updates = buildUpdates();
      const appliedPagePrompt = updates.some(
        u => contexts.find(c => c.id === u.id)?.capability === 'NER' && drafts[u.id].applyPage
      );
      // The backend also records this deploy in the message's version history.
      const res = await api.post('/cdt-tenants/deploy', {
        ...creds,
        contexts: updates,
        system_message_id: messageId,
        system_message: systemMessage,
        response_format: responseFormat,
        applied_page_prompt: appliedPagePrompt,
        deployed_by: deployedBy
      });
      const updated = res.data.updated || [];
      setHistoryNote(res.data.history_warning
        ? { warning: true, text: res.data.history_warning }
        : { warning: false, text: `Recorded in History as v${res.data.history_version}.` });
      const byId = Object.fromEntries(updated.map(c => [c.id, c]));
      const next = contexts.map(c => byId[c.id] || c);
      setContexts(next);
      setDrafts(Object.fromEntries(next.map(c => [c.id, toDraft(c)])));
      setDeployed(updated);
      setConfirming(false);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Deploy failed');
      setConfirming(false);
    } finally {
      setDeploying(false);
    }
  };

  const nerCount = contexts.filter(c => c.capability === 'NER').length;

  const renderConfigField = (ctxId, key, f) => {
    if (f.type === 'boolean') {
      return (
        <Dropdown
          value={String(f.value)}
          options={[['true', 'True'], ['false', 'False']]}
          onChange={v => setField(ctxId, key, v === 'true')}
        />
      );
    }
    if (SELECT_OPTIONS[key]) {
      // Keep a value CDT already has even if it isn't in our list, so loading never silently changes it.
      const options = SELECT_OPTIONS[key].some(([v]) => v === f.value)
        ? SELECT_OPTIONS[key]
        : [...SELECT_OPTIONS[key], [f.value, f.value]];
      return <Dropdown value={f.value} options={options} onChange={v => setField(ctxId, key, v)} />;
    }
    if (f.type === 'json' || f.type === 'lines') {
      return (
        <McTextarea
          label={humanize(key)}
          hiddenlabel
          rows={4}
          value={f.value}
          input={e => setField(ctxId, key, e.target.value)}
          placeholder={f.type === 'lines' ? 'One per line' : ''}
        />
      );
    }
    return (
      <McInput
        label={humanize(key)}
        hiddenlabel
        placeholder=""
        type={f.type === 'number' ? 'number' : 'text'}
        value={f.value}
        input={e => setField(ctxId, key, e.target.value)}
      />
    );
  };

  const renderContext = (ctx) => {
    const draft = drafts[ctx.id];
    if (!draft) return null;
    const isNer = ctx.capability === 'NER';
    const configEntries = Object.entries(draft.fields);
    return (
      <div key={ctx.id} style={{ ...card, border: `1px solid ${isNer ? COLORS.accent : COLORS.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, marginBottom: SPACING.md }}>
          <CapTag cap={ctx.capability} />
          <span className="mds-text--medium-bold" style={{ color: COLORS.text }}>
            {ctx.name}
          </span>
          <span className="mds-text--x-small-normal" style={{ marginLeft: 'auto', color: COLORS.textSecondary }}>
            {ctx.id}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: SPACING.md, marginBottom: SPACING.md }}>
          <Field label="Name">
            <McInput label="Name" hiddenlabel placeholder="" value={draft.name} input={e => setDraft(ctx.id, { name: e.target.value })} maxlength={256} />
          </Field>
          <Field label="Version">
            <McInput label="Version" hiddenlabel placeholder="" type="number" min={1} value={draft.version} input={e => setDraft(ctx.id, { version: e.target.value })} />
          </Field>
          <Field label="Description" wide>
            <McTextarea label="Description" hiddenlabel placeholder="" rows={2} value={draft.description} input={e => setDraft(ctx.id, { description: e.target.value })} maxlength={1024} />
          </Field>
        </div>

        {isNer && (
          <div style={{
            padding: SPACING.md,
            backgroundColor: COLORS.infoBg,
            borderRadius: BORDER_RADIUS.md,
            marginBottom: SPACING.md
          }}>
            <McCheckbox
              label="Use the system prompt & response format from this page"
              checked={draft.applyPage}
              change={e => setDraft(ctx.id, { applyPage: e.target.checked })}
            />
            <div className="mds-text--x-small-normal" style={{ marginTop: SPACING.xs, color: COLORS.textSecondary }}>
              {draft.applyPage
                ? `Replaces the CDT systemPrompt (${(ctx.config?.systemPrompt || '').length} → ${systemMessage.length} chars) and responseFormat with what's on "${messageName || 'this page'}" right now, including unsaved edits.`
                : 'The CDT systemPrompt and responseFormat stay as they are.'}
            </div>
          </div>
        )}

        <div className="mds-text--x-small-bold" style={{ ...labelStyle, marginBottom: SPACING.sm }}>Configuration</div>
        {configEntries.length ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.md }}>
            {configEntries.map(([key, f]) => (
              <Field key={key} label={humanize(key)} wide={f.type === 'json' || f.type === 'lines'}>
                {renderConfigField(ctx.id, key, f)}
              </Field>
            ))}
          </div>
        ) : (
          <div className="mds-text--small-normal" style={{ color: COLORS.textSecondary }}>No configuration fields.</div>
        )}
      </div>
    );
  };

  return (
    <McModal open heading="Deploy to CDT" width="860px" height="92vh" closed={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.lg }}>
        <div className="mds-text--small-normal" style={{ color: COLORS.textSecondary }}>
          Update your Neurix workspace's workflow directly — no ticket needed.
        </div>
        <LiveWarning />

        {/* Your Lab sign-in is used for CDT; only the IDs are remembered */}
        <div style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: SPACING.md }}>
            <Field label="Workspace ID" required>
              <McInput label="Workspace ID" hiddenlabel value={creds.workspace_id} input={e => setCred('workspace_id', e.target.value)} placeholder="Workspace ID" />
            </Field>
            <Field label="Workflow ID" required>
              <McInput label="Workflow ID" hiddenlabel value={creds.workflow_id} input={e => setCred('workflow_id', e.target.value)} placeholder="Workflow ID" />
            </Field>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: SPACING.md }}>
            <MaerskButton onClick={handleLoad} disabled={!credsReady || loading} loading={loading}>
              {loading ? 'Loading…' : workflow ? 'Reload Workflow' : 'Load Workflow'}
            </MaerskButton>
          </div>
        </div>

        {access.error && (
          <McNotification appearance="error" heading="You can't deploy this instruction" fit="small">{access.error}</McNotification>
        )}

        {error && (
          <McNotification appearance="error" fit="small">{error}</McNotification>
        )}

        {deployed && (
          <McNotification appearance="success" fit="small" icon="check-circle">
            Deployed {deployed.length} application context{deployed.length !== 1 ? 's' : ''} — your CDT workflow is using the new configuration now.
            {historyNote && !historyNote.warning && <> {historyNote.text}</>}
          </McNotification>
        )}

        {deployed && historyNote?.warning && (
          <McNotification appearance="warning" fit="small">{historyNote.text}</McNotification>
        )}

        {workflow && (
          <>
            <div style={card}>
              <div className="mds-text--medium-bold" style={{ color: COLORS.text }}>
                {workflow.name || '(unnamed workflow)'}
              </div>
              <div className="mds-text--x-small-normal" style={{ color: COLORS.textSecondary, marginTop: 4 }}>
                {[workflow.documentType, workflow.workflowType, `v${workflow.version || 1}`].filter(Boolean).join(' · ')}
              </div>
              <div style={{ display: 'flex', gap: SPACING.xs, marginTop: SPACING.sm, flexWrap: 'wrap', alignItems: 'center' }}>
                {(workflow.pipeline || []).map((s, i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <span style={{ color: COLORS.textSecondary }}>→</span>}
                    <CapTag cap={s.capability} />
                  </React.Fragment>
                ))}
              </div>
              {nerCount === 0 && (
                <div className="mds-text--small-normal" style={{ marginTop: SPACING.sm, color: COLORS.warningText }}>
                  This workflow has no NER application context, so this page's prompt and schema have nowhere to go.
                  You can still edit the other capabilities below.
                </div>
              )}
            </div>
            {contexts.map(renderContext)}
          </>
        )}
      </div>

      {/* Footer */}
      <div slot="footer" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: SPACING.md, width: '100%' }}>
        {confirming ? (
          <>
            <span className="mds-text--small-normal" style={{ marginRight: 'auto', color: COLORS.warningText, fontWeight: FONT_WEIGHTS.medium }}>
              This goes live on CDT immediately. Deploy now?
            </span>
            <MaerskButton variant="outline" onClick={() => setConfirming(false)} disabled={deploying}>Back</MaerskButton>
            <MaerskButton onClick={handleDeploy} disabled={deploying} loading={deploying}>
              {deploying ? 'Deploying…' : 'Yes, deploy now'}
            </MaerskButton>
          </>
        ) : (
          <>
            <MaerskButton variant="outline" onClick={onClose}>Close</MaerskButton>
            <MaerskButton onClick={handleReview} disabled={!access.allowed || !workflow || !contexts.length || loading}>
              Deploy to CDT
            </MaerskButton>
          </>
        )}
      </div>
    </McModal>
  );
};

export default DeployToCdt;
